import { Password, UserId } from "@server/models/auth.js";
import { z } from "zod";

// TODO: Split it into models/ folder

export const SortOrder = z.compile(z.enum(["asc", "desc"]));
export type SortOrder = z.infer<typeof SortOrder>;

export const TimestampISO = z.compile(z.iso.datetime());
export type TimestampISO = z.infer<typeof TimestampISO>;

export type SortBy<T> = {
  order: SortOrder;
  by: T;
};

/**
 * TODO: Accept negative and zero values (EmployeeSalary will handle nonnegative)
 */
export const AmountInCents = z.compile(z.int().nonnegative());
export type AmountInCents = z.infer<typeof AmountInCents>;

export const EmployeeSalary = z.compile(z.int().nonnegative());
export type EmployeeSalary = z.infer<typeof EmployeeSalary>;

export type EmployeeNameAndSalary = {
  name: string;
  salaryInCents: EmployeeSalary;
};

export const cpfLength = 11;
export const CPF = z.compile(z.string().length(cpfLength));

export const EnterpriseId = z.compile(z.string().nonempty());
export type EnterpriseId = z.infer<typeof EnterpriseId>;

export const StoreId = z.compile(z.string().nonempty());
export type StoreId = z.infer<typeof StoreId>;

export const ModuleId = z.compile(
  z.enum(["employees", "early-payments", "erp"]),
);
export type ModuleId = z.infer<typeof ModuleId>;

const EnterpriseOwnerAccess = z.compile(
  z.object({
    role: z.literal("enterpriseOwner"),
    enterprisesIds: z.array(EnterpriseId).nonempty(),
    storesIds: z.array(StoreId).optional(),
  }),
);
export type EnterpriseOwnerAccess = z.infer<typeof EnterpriseOwnerAccess>;

const StoreManagerAccess = z.compile(
  z.object({
    role: z.literal("storeManager"),
    storesIds: z.array(StoreId).nonempty(),
  }),
);
export type StoreManagerAccess = z.infer<typeof StoreManagerAccess>;

const AuthAccess = z.compile(
  z.object({
    role: z.literal("auth"),
  }),
);
export type AuthAccess = z.infer<typeof AuthAccess>;

const AdminAccess = z.compile(
  z.object({
    role: z.literal("admin"),
  }),
);
export type AdminAccess = z.infer<typeof AdminAccess>;

export const UserAccess = z.compile(
  z.discriminatedUnion("role", [
    EnterpriseOwnerAccess,
    StoreManagerAccess,
    AdminAccess,
    AuthAccess,
  ]),
);
export type UserAccess = z.infer<typeof UserAccess>;

export const UserAccessRole = z.union(
  UserAccess.options.map((option) => option.shape.role),
);
export type UserAccessRole = z.infer<typeof UserAccessRole>;

export const EmployeeRoleId = z.compile(z.string().nonempty());

export const EmployeeRole = z.compile(
  z.object({
    id: EmployeeRoleId,
    name: z.string(),
    suggestedSalaryInCents: AmountInCents,
  }),
);
export type EmployeeRole = z.infer<typeof EmployeeRole>;

// TODO: Remove it
const plainDateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
function isPlainDate(value: string): boolean {
  const match = plainDateRegex.exec(value);
  if (!match?.[1] || !match[2] || !match[3]) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * @deprecated Use Temporal.PlainDate instead
 */
export const DayOfTheMonthISO = z.compile(
  z.string().refine(isPlainDate, "Data inválida"),
);
/**
 * @deprecated Use Temporal.PlainDate instead
 */
export type DayOfTheMonthISO = z.infer<typeof DayOfTheMonthISO>;

export type Range<T> = {
  start: T;
  end: T;
};

export const Store = z.compile(
  z.object({
    id: StoreId,
    name: z.string(),
  }),
);
export type Store = z.infer<typeof Store>;

export const ChangeStoreNameInput = z.compile(
  z.object({
    id: z.string(),
    newName: z.string(),
  }),
);
export type ChangeStoreNameInput = z.infer<typeof ChangeStoreNameInput>;

export const EmployeesSortFields = z.compile(
  z.enum(["name", "hireDate", "updatedAt"]),
);
export type EmployeesSortFields = z.infer<typeof EmployeesSortFields>;

export const EmployeeSearchType = z.compile(z.enum(["cpf", "name"]));
export type EmployeeSearchType = z.infer<typeof EmployeeSearchType>;

export const EmployeeId = z.compile(z.string().nonempty());
export type EmployeeId = z.infer<typeof EmployeeId>;

export const Employee = z.compile(
  z.object({
    id: EmployeeId,
    name: z.string(),
    cpf: CPF,
    isActive: z.boolean(),
    hireDate: DayOfTheMonthISO.nullish(),
    updatedAt: z.date().nullish(),
    enterpriseId: z.string(),
    salaryInCents: AmountInCents,
    roleId: EmployeeRoleId,
  }),
);
export type Employee = z.infer<typeof Employee>;

export const EmployeeDetails = z.compile(
  Employee.extend({
    storesIds: z.array(StoreId),
  }),
);
export type EmployeeDetails = z.infer<typeof EmployeeDetails>;

export const EmployeeListItem = z.compile(
  Employee.pick({
    id: true,
    name: true,
    isActive: true,
    hireDate: true,
    updatedAt: true,
  }).extend({
    storesNames: z.array(z.string()),
  }),
);
export type EmployeeListItem = z.infer<typeof EmployeeListItem>;

export const UpdateEmployeeInput = z.compile(
  Employee.omit({
    id: true,
  })
    .partial()
    .extend({
      id: z.string(),
      storesIds: z.array(StoreId).optional(),
      roleId: StoreId.optional(),
    }),
);
export type UpdateEmployeeInput = z.infer<typeof UpdateEmployeeInput>;

const EmployeeRecordActionType = z.compile(
  z.enum(["create", "update", "soft-delete", "soft-restore"]),
);
export type EmployeeRecordActionType = z.infer<typeof EmployeeRecordActionType>;

export const EmployeeUpdatableField = z.compile(
  UpdateEmployeeInput.pick({
    name: true,
    cpf: true,
    roleId: true,
    salaryInCents: true,
    enterpriseId: true,
    hireDate: true,
    storesIds: true,
  }).keyof(),
);
export type EmployeeUpdatableField = z.infer<typeof EmployeeUpdatableField>;

const EmployeeRecordBase = z.compile(
  z.object({
    id: z.string(),
    performedAt: z.date(),
    performedBy: z.object({
      id: z.string(),
      name: z.string(),
    }),
    employeeId: EmployeeId,
  }),
);

const EmployeeRecordUpdate = z.compile(
  EmployeeRecordBase.extend({
    actionType: z.literal("update"),
    updatedFields: z.array(EmployeeUpdatableField),
  }),
);
const EmployeeRecordCreate = z.compile(
  EmployeeRecordBase.extend({
    actionType: z.literal("create"),
  }),
);
const EmployeeRecordSoftDelete = z.compile(
  EmployeeRecordBase.extend({
    actionType: z.literal("soft-delete"),
  }),
);
const EmployeeRecordSoftRestore = z.compile(
  EmployeeRecordBase.extend({
    actionType: z.literal("soft-restore"),
  }),
);

export const EmployeeRecord = z.compile(
  z.discriminatedUnion("actionType", [
    EmployeeRecordUpdate,
    EmployeeRecordCreate,
    EmployeeRecordSoftDelete,
    EmployeeRecordSoftRestore,
  ]),
);
export type EmployeeRecord = z.infer<typeof EmployeeRecord>;

export const AddEmployeeRecord = z.compile(
  z.discriminatedUnion("actionType", [
    EmployeeRecordUpdate.omit({ id: true, performedBy: true }).extend({
      performedBy: UserId,
    }),
    EmployeeRecordCreate.omit({ id: true, performedBy: true }).extend({
      performedBy: UserId,
    }),
    EmployeeRecordSoftDelete.omit({ id: true, performedBy: true }).extend({
      performedBy: UserId,
    }),
    EmployeeRecordSoftRestore.omit({ id: true, performedBy: true }).extend({
      performedBy: UserId,
    }),
  ]),
);
export type AddEmployeeRecord = z.infer<typeof AddEmployeeRecord>;

export const HiFormFieldId = z.compile(z.string().nonempty());
export type HiFormFieldId = z.infer<typeof HiFormFieldId>;

export const HiFormFieldValue = z.compile(z.unknown());
export type HiFormFieldValue = z.infer<typeof HiFormFieldValue>;
export const HiFormValues = z.compile(
  z.record(HiFormFieldId, HiFormFieldValue),
);
export type HiFormValues = z.infer<typeof HiFormValues>;

export const HiFormFieldObligatoriness = z.compile(
  z.enum(["required", "optional", "pending"]),
);
export type HiFormFieldObligatoriness = z.infer<
  typeof HiFormFieldObligatoriness
>;

export const HiFormFieldDataType = z.compile(
  z.enum(["text", "date", "image", "singleSelection"]),
);
export type HiFormFieldDataType = z.infer<typeof HiFormFieldDataType>;

const HiFormFieldBase = z.compile(
  z.object({
    id: z.string(),
    name: z.string(),
    obligatoriness: HiFormFieldObligatoriness,
    formPosition: z.number().min(0),
  }),
);

export const HiFormFieldText = z.compile(
  HiFormFieldBase.extend({
    type: z.literal("text"),
  }),
);
export type HiFormFieldText = z.infer<typeof HiFormFieldText>;

export const HiFormFieldDate = z.compile(
  HiFormFieldBase.extend({
    type: z.literal("date"),
  }),
);
export type HiFormFieldDate = z.infer<typeof HiFormFieldDate>;

export const HiFormFieldFile = z.compile(
  HiFormFieldBase.extend({
    type: z.literal("image"),
  }),
);
export type HiFormFieldFile = z.infer<typeof HiFormFieldFile>;

export const HiFormFieldSingleSelection = z.compile(
  HiFormFieldBase.extend({
    type: z.literal("singleSelection"),
    options: z.array(z.string()).nonempty(),
  }),
);
export type HiFormFieldSingleSelection = z.infer<
  typeof HiFormFieldSingleSelection
>;

export const HiFormField = z.compile(
  z.discriminatedUnion("type", [
    HiFormFieldText,
    HiFormFieldDate,
    HiFormFieldSingleSelection,
    HiFormFieldFile,
  ]),
);
export type HiFormField = z.infer<typeof HiFormField>;

export const HiForm = z.compile(
  z.object({
    id: z.string(),
    name: z.string(),
    fields: z.array(HiFormField),
  }),
);
export type HiForm = z.infer<typeof HiForm>;

export const Pagination = z.compile(
  z.object({
    page: z.number().min(0),
    pageSize: z.number().min(1),
  }),
);
export type Pagination = z.infer<typeof Pagination>;

export const PaginationResult = z.compile(
  Pagination.extend({
    pageCount: z.number().min(1),
    itemsCount: z.number().min(0),
  }),
);

export const PaginationRange = z.compile(
  z.object({
    fromRow: z.number().min(0),
    toRow: z.number().min(0),
  }),
);
export type PaginationRange = z.infer<typeof PaginationRange>;

export const createPaginatedSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.compile(
    z.object({
      items: z.array(itemSchema),
      pagination: PaginationResult,
    }),
  );

export type Paginated<T = unknown> = z.infer<
  ReturnType<typeof createPaginatedSchema<z.ZodType<T>>>
>;

export const Enterprise = z.compile(
  z.object({
    id: EnterpriseId,
    name: z.string().nonempty(),
    ownersIds: z.array(z.string().nonempty()),
  }),
);
export type Enterprise = z.infer<typeof Enterprise>;

export const EnterpriseListItem = z.compile(
  Enterprise.pick({ id: true, name: true }),
);
export type EnterpriseListItem = z.infer<typeof EnterpriseListItem>;

export const ManagedUser = z.compile(
  z.object({
    id: UserId,
    name: z.string().nonempty(),
    email: z.email(),
    role: UserAccessRole,
    storesIds: z.array(StoreId),
  }),
);
export type ManagedUser = z.infer<typeof ManagedUser>;

const ManagedUserRole = z.compile(z.enum(["enterpriseOwner", "storeManager"]));
const UniqueStoreIds = z.compile(
  z
    .array(StoreId)
    .refine(
      (storesIds) => new Set(storesIds).size === storesIds.length,
      "As lojas não podem ser repetidas",
    ),
);

export const CreateManagedUserInput = z.compile(
  z.object({
    enterpriseId: EnterpriseId,
    name: z.string().trim().min(1),
    email: z.email(),
    password: Password,
    role: ManagedUserRole.default("storeManager"),
    storesIds: UniqueStoreIds.default([]),
  }),
);
export type CreateManagedUserInput = z.infer<typeof CreateManagedUserInput>;

export const CreateTestUserInput = z.compile(
  z.object({
    name: z.string().trim().min(1),
    email: z.email(),
    password: Password,
  }),
);
export type CreateTestUserInput = z.infer<typeof CreateTestUserInput>;

export const TestUser = z.compile(
  z.object({
    user: ManagedUser,
    enterprise: EnterpriseListItem,
    stores: z.array(Store),
  }),
);
export type TestUser = z.infer<typeof TestUser>;

export const UpdateManagerStoresInput = z.compile(
  z.object({
    enterpriseId: EnterpriseId,
    userId: UserId,
    storesIds: UniqueStoreIds,
  }),
);
export type UpdateManagerStoresInput = z.infer<typeof UpdateManagerStoresInput>;

export const PromoteManagerToOwnerInput = z.compile(
  z.object({
    enterpriseId: EnterpriseId,
    userId: UserId,
  }),
);
export type PromoteManagerToOwnerInput = z.infer<
  typeof PromoteManagerToOwnerInput
>;

export const DemoteOwnerToManagerInput = z.compile(
  z.object({
    enterpriseId: EnterpriseId,
    userId: UserId,
    storesIds: UniqueStoreIds,
  }),
);
export type DemoteOwnerToManagerInput = z.infer<
  typeof DemoteOwnerToManagerInput
>;

export const EnterpriseUsageCounts = z.compile(
  z.object({
    name: z.string().nonempty(),
    storesCount: z.number().int().nonnegative(),
    employeesCount: z.number().int().nonnegative(),
    activeEmployeesCount: z.number().int().nonnegative(),
    inactiveEmployeesCount: z.number().int().nonnegative(),
    xlsxExportsCount: z.number().int().nonnegative(),
    earlyPaymentRequestsCount: z.number().int().nonnegative(),
    approvedEarlyPaymentsInCents: AmountInCents,
    rejectedEarlyPaymentsInCents: AmountInCents,
  }),
);
export type EnterpriseUsageCounts = z.infer<typeof EnterpriseUsageCounts>;

export const EnterpriseFileGroupUsage = z.compile(
  z.object({
    filesCount: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative(),
  }),
);
export type EnterpriseFileGroupUsage = z.infer<typeof EnterpriseFileGroupUsage>;

export const EnterpriseFileUsage = z.compile(
  EnterpriseFileGroupUsage.extend({
    bytesPerEmployee: z.number().int().nonnegative(),
    documents: EnterpriseFileGroupUsage,
    images: EnterpriseFileGroupUsage,
    spreadsheets: EnterpriseFileGroupUsage,
  }),
);
export type EnterpriseFileUsage = z.infer<typeof EnterpriseFileUsage>;

export const EnterpriseUsageOverview = z.compile(
  EnterpriseUsageCounts.extend({
    fileUsage: EnterpriseFileUsage,
  }),
);
export type EnterpriseUsageOverview = z.infer<typeof EnterpriseUsageOverview>;

export const CreateEmployeeInput = z.compile(
  Employee.omit({
    id: true,
    updatedAt: true,
  }).extend({
    storesIds: z.array(StoreId),
    roleId: StoreId,
  }),
);
export type CreateEmployeeInput = z.infer<typeof CreateEmployeeInput>;

export const UpdateEmployeeRoleInput = z.compile(
  z.object({
    id: z.string(),
    name: z.string().nonempty(),
    suggestedSalaryInCents: AmountInCents,
  }),
);
export type UpdateEmployeeRoleInput = z.infer<typeof UpdateEmployeeRoleInput>;

export const AddEmployeeRoleInput = z.compile(
  z.object({
    name: z.string().nonempty(),
    suggestedSalaryInCents: AmountInCents,
    enterpriseId: EnterpriseId,
  }),
);
export type AddEmployeeRoleInput = z.infer<typeof AddEmployeeRoleInput>;

export const EarlyPaymentRequestId = z.compile(z.string());
export type EarlyPaymentRequestId = z.infer<typeof EarlyPaymentRequestId>;

export const EarlyPaymentParameterName = z.compile(
  z.string().trim().min(1).max(100),
);
export type EarlyPaymentParameterName = z.infer<
  typeof EarlyPaymentParameterName
>;

const EarlyPaymentParameterNames = z.compile(
  z
    .array(EarlyPaymentParameterName)
    .refine(
      (names) =>
        new Set(names.map((name) => name.toLocaleLowerCase())).size ===
        names.length,
      "Os nomes não podem ser repetidos",
    ),
);

export const EarlyPaymentParameters = z.compile(
  z.object({
    requestTypes: EarlyPaymentParameterNames,
    requestCategories: EarlyPaymentParameterNames,
  }),
);
export type EarlyPaymentParameters = z.infer<typeof EarlyPaymentParameters>;

export const UpdateEarlyPaymentParametersInput = z.compile(
  EarlyPaymentParameters.extend({ enterpriseId: EnterpriseId }),
);
export type UpdateEarlyPaymentParametersInput = z.infer<
  typeof UpdateEarlyPaymentParametersInput
>;

export const ExtraFormField = z.compile(
  z.object({
    fieldId: HiFormFieldId,
    formId: z.string().nonempty(),
    name: z.string(),
  }),
);
export type ExtraFormField = z.infer<typeof ExtraFormField>;

export const UpdateExtraFormFieldInput = z.compile(
  z.object({
    enterpriseId: EnterpriseId,
    fieldId: HiFormFieldId.nullable(),
  }),
);
export type UpdateExtraFormFieldInput = z.infer<
  typeof UpdateExtraFormFieldInput
>;

export const EarlyPaymentRequestActionStatus = z.compile(
  z.enum(["request", "approve", "reject"]),
);
export type EarlyPaymentRequestActionStatus = z.infer<
  typeof EarlyPaymentRequestActionStatus
>;

export const EarlyPaymentRequest = z.compile(
  z.object({
    id: EarlyPaymentRequestId,
    enterpriseId: EnterpriseId,
    requestedFor: EmployeeId,
    requestedBy: UserId,
    requestedAt: DayOfTheMonthISO,
    amountInCents: AmountInCents,
    requestType: z.nullish(EarlyPaymentParameterName),
    requestCategory: z.nullish(EarlyPaymentParameterName),
    status: EarlyPaymentRequestActionStatus,
    updatedAt: z.nullish(DayOfTheMonthISO),
    updatedBy: z.nullish(UserId),
  }),
);
export type EarlyPaymentRequest = z.infer<typeof EarlyPaymentRequest>;

export const ReviewableEarlyPaymentRequestData = z.compile(
  EarlyPaymentRequest.extend({
    requestedForName: z.string().nonempty(),
    requestedByName: z.string().nonempty(),
    updatedByName: z.string().nonempty().nullable(),
    employeeSalaryInCents: AmountInCents,
    extraFormFieldValue: z.string().nonempty().nullable(),
  }),
);
export type ReviewableEarlyPaymentRequestData = z.infer<
  typeof ReviewableEarlyPaymentRequestData
>;

export const ReviewableEarlyPaymentRequest = z.compile(
  ReviewableEarlyPaymentRequestData.extend({
    usedInCents: AmountInCents,
  }),
);
export type ReviewableEarlyPaymentRequest = z.infer<
  typeof ReviewableEarlyPaymentRequest
>;

export type EarlyPaymentListRequestsFilters = {
  requestedBy?: UserId;
  requestedFor?: EmployeeId;
  statuses?: EarlyPaymentRequestActionStatus[];
};

export const CreateEarlyPaymentRequestInput = z.compile(
  z.object({
    requestedFor: EmployeeId,
    requestedBy: UserId,
    requestedAt: DayOfTheMonthISO,
    amountInCents: AmountInCents,
    requestType: EarlyPaymentParameterName,
    requestCategory: EarlyPaymentParameterName,
  }),
);
export type CreateEarlyPaymentRequestInput = z.infer<
  typeof CreateEarlyPaymentRequestInput
>;

export const UpdateEarlyPaymentRequestInput = z.compile(
  z.object({
    status: EarlyPaymentRequestActionStatus.exclude(["request"]),
    updatedAt: TimestampISO,
    updatedBy: UserId,
  }),
);
export type UpdateEarlyPaymentRequestInput = z.infer<
  typeof UpdateEarlyPaymentRequestInput
>;

export const EarlyPaymentRequestsData = z.compile(
  z.object({
    employeeId: EmployeeId,
    previousRequests: z.array(EarlyPaymentRequest),
    parameters: EarlyPaymentParameters,
  }),
);
export type EarlyPaymentRequestsData = z.infer<typeof EarlyPaymentRequestsData>;

export const PaginatedReviewableEarlyPaymentRequests = createPaginatedSchema(
  ReviewableEarlyPaymentRequest,
);
export type PaginatedReviewableEarlyPaymentRequests = z.infer<
  typeof PaginatedReviewableEarlyPaymentRequests
>;

export const ReviewableEarlyPaymentRequestsPage = z.compile(
  z.object({
    items: z.array(ReviewableEarlyPaymentRequest),
    pagination: PaginationResult,
  }),
);
export type ReviewableEarlyPaymentRequestsPage = z.infer<
  typeof ReviewableEarlyPaymentRequestsPage
>;

export type EarlyPaymentCycle = {
  start: Temporal.PlainDate;
  endExclusive: Temporal.PlainDate;
};
